/**
 * SearchAutocomplete — ricerca corsi con suggerimenti non vincolanti
 *
 * Campo vuoto → riga di aiuto con esempi cliccabili e link "Tutte le professioni", che allarga
 * il menu in un pannello con l'elenco completo (window.IKOS.occupations).
 * Mentre si digita → suggerimenti: professioni (confronto locale) e titoli corsi (dall'API).
 * Enter → submit form (ricerca testuale normale). Click su suggerimento → pagina professione
 * o scheda corso.
 *
 * Uso:
 *   <div x-data="searchAutocomplete" class="ik-input-group">
 *     <input type="text" x-model="query" @input.debounce.300ms="fetchSuggestions"
 *            @keydown.escape="closeSuggestions" @keydown.arrow-down.prevent="highlightNext"
 *            @keydown.arrow-up.prevent="highlightPrev" @keydown.enter="onEnter">
 *     ...
 *   </div>
 */
/** Confronto senza accenti e maiuscole */
function fold(text) {
    return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}


export function registerSearchAutocomplete(Alpine) {
    Alpine.data('searchAutocomplete', (initialQuery = '') => ({
        query: initialQuery,
        suggestions: [],
        /** Cosa mostra il menu: 'hint' (campo vuoto), 'list' (suggerimenti), 'panel' (tutte le professioni) */
        mode: 'list',
        open: false,
        highlighted: -1,
        loading: false,
        abortController: null,

        /** Campo vuoto (focus o testo cancellato): riga di aiuto con esempi e link al pannello. */
        showDefaults() {
            if (this.mode !== 'panel') {
                this.mode = 'hint';
            }
            this.suggestions = [];
            this.open = true;
            this.highlighted = -1;
        },

        onFocus() {
            if (this.query.trim() === '') {
                this.showDefaults();
            } else if (this.suggestions.length) {
                this.mode = 'list';
                this.open = true;
            }
        },

        /** Esempio cliccato nella riga di aiuto: lancia la ricerca (una professione porta alla sua pagina). */
        useExample(term) {
            this.query = term;
            this.open = false;
            this.$dispatch('search-submit', { query: term });
        },

        openPanel() {
            this.mode = 'panel';
            this.open = true;
            this.highlighted = -1;
        },

        /** Tutte le professioni ECM in pagina (window.IKOS.occupations), in ordine alfabetico. */
        allOccupations() {
            const list = Array.isArray(window.IKOS?.occupations) ? window.IKOS.occupations : [];
            // Nome SEO al plurale quando c'e' ("Massofisioterapisti" invece del nome ministeriale lungo)
            const label = o => o.name_seo || o.name;
            return [...list].sort((a, b) => label(a).localeCompare(label(b), 'it'));
        },

        occupationUrl(o) {
            return this.catalogBase() + o.slug + '/';
        },

        /** Base URL del catalogo corrente: /corsi-ecm/, /corsi-ecm/fad/ o /corsi-ecm/residenziali/ */
        catalogBase() {
            const typeMatch = window.location.pathname.match(/\/corsi-ecm\/(fad|residenziali)\//);
            return '/corsi-ecm/' + (typeMatch ? typeMatch[1] + '/' : '');
        },

        async fetchSuggestions() {
            const q = this.query.trim();
            if (q.length === 0) {
                if (this.abortController) this.abortController.abort();
                this.showDefaults();
                return;
            }
            if (q.length < 2) {
                this.closeSuggestions();
                return;
            }

            // Cancella richiesta precedente
            if (this.abortController) {
                this.abortController.abort();
            }
            this.abortController = new AbortController();

            // Professioni: confronto locale sull'elenco gia' in pagina, subito, senza chiamate.
            const occupations = this.matchOccupations(q);

            this.loading = true;
            try {
                const apiBase = window.IKOS?.apiBase || '';
                const url = `${apiBase}/v1/courses?search=${encodeURIComponent(q)}&page_size=5`;
                const res = await fetch(url, {
                    // Solo header "semplici": con Content-Type il browser faceva un preflight
                    // OPTIONS a ogni tasto digitato.
                    headers: { 'Accept': 'application/json' },
                    signal: this.abortController.signal,
                });

                if (!res.ok) throw new Error(res.status);

                const data = await res.json();
                const items = data?._embedded?.items || [];

                const courses = items.map(c => ({
                    kind: 'course',
                    id: 'c' + c.id,
                    title: c.short_title || c.title,
                    url: '/' + (c.urlkey || '') + '.html',
                    credit: c.credit || 0,
                    type: c.type || 'FAD',
                    code: c.code || '',
                }));

                // Una lista sola (professioni prima) cosi' frecce e invio scorrono entrambi i gruppi.
                this.suggestions = [...occupations, ...courses];
                this.mode = 'list';
                this.open = this.suggestions.length > 0;
                this.highlighted = -1;
            } catch (e) {
                if (e.name !== 'AbortError') {
                    this.suggestions = occupations;
                    this.mode = 'list';
                    this.open = occupations.length > 0;
                    this.highlighted = -1;
                }
            } finally {
                this.loading = false;
            }
        },

        /**
         * Professioni il cui nome (o nome al plurale) contiene il testo digitato, massimo 3.
         * Il link va alla pagina professione, conservando FAD/residenziali della pagina corrente.
         */
        matchOccupations(q) {
            const needle = fold(q);
            const list = Array.isArray(window.IKOS?.occupations) ? window.IKOS.occupations : [];
            const base = this.catalogBase();
            return list
                .filter(o => fold(o.name).includes(needle) || fold(o.name_seo).includes(needle))
                .slice(0, 3)
                .map(o => ({
                    kind: 'occupation',
                    id: 'o' + o.slug,
                    title: o.name,
                    url: base + o.slug + '/',
                }));
        },

        groupLabel(kind) {
            return { occupation: 'Professioni', course: 'Corsi' }[kind] || '';
        },

        closeSuggestions() {
            this.open = false;
            this.mode = 'list';
            this.suggestions = [];
            this.highlighted = -1;
        },

        highlightNext() {
            if (!this.open || !this.suggestions.length) return;
            this.highlighted = (this.highlighted + 1) % this.suggestions.length;
        },

        highlightPrev() {
            if (!this.open || !this.suggestions.length) return;
            this.highlighted = this.highlighted <= 0
                ? this.suggestions.length - 1
                : this.highlighted - 1;
        },

        onEnter(e) {
            if (this.highlighted >= 0 && this.open) {
                // Se c'è un suggerimento evidenziato, naviga al corso
                e.preventDefault();
                this.goToCourse(this.suggestions[this.highlighted]);
            }
            // Altrimenti: lascia il submit del form (ricerca testuale)
        },

        goToCourse(item) {
            window.location.href = item.url;
        },

        courseUrl(item) {
            return item.url;
        },
    }));
}
